import {Trans} from '@lingui/macro';
import React, {FC, useMemo} from 'react';

import {SuggestionSelectHandler} from '../../../../../../srcCommon/inboxes/search/searchHelpers';
import {useAppSelector} from '../../../../../helpers/reduxHelpers';
import {getCellsSearchRecentQueries} from '../../../../../store/panels/cells/search/cellsPanelSearchSelectors';
import {FuzzyHighlight} from '../../../cells/header/search/highlight/fuzzyHighlight';
import {
  InboxesSuggestionsDropdownItem,
  InboxesSuggestionsDropdownItemIcon,
  InboxesSuggestionsDropdownItemPrimaryContent,
  InboxesSuggestionsDropdownSection,
  InboxesSuggestionsDropdownSectionTitle,
  InboxesSuggestionsDropdownSectionWrapper,
} from './inboxesSearchSuggestionsDropdownCommons';

/*
 * Constants.
 */

const MAX_DISPLAYED_RECENT_QUERIES = 3;

/*
 * Props.
 */

interface InboxesSearchSuggestionsDropdownQueryProps {
  query?: string;
  onSuggestionSelect: SuggestionSelectHandler;
}

/*
 * Component.
 */

export const InboxesSearchSuggestionsDropdownQuery: FC<InboxesSearchSuggestionsDropdownQueryProps> = ({
  query,
  onSuggestionSelect,
}) => {
  const recentQueries = useAppSelector(getCellsSearchRecentQueries);

  const filteredRecentQueries = useMemo(() => {
    const sanitizedQuery = query ? query.replace(/\s+/g, ' ') : '';
    return (recentQueries || []).filter(
      (recentQuery) => recentQuery.startsWith(sanitizedQuery) && recentQuery !== query,
    );
  }, [query, recentQueries]);

  // Return early if query is not defined and there's no recent query.
  if (!query && filteredRecentQueries.length === 0) {
    return null;
  }

  return (
    <>
      {query && (
        <InboxesSuggestionsDropdownSectionWrapper>
          <InboxesSuggestionsDropdownSection>
            <InboxesSuggestionsDropdownItem
              onSelect={(e, key) =>
                onSuggestionSelect &&
                onSuggestionSelect(query, key, {
                  insertWhiteSpace: true,
                  replaceWholeQuery: true,
                  isCurrentQuery: true,
                })
              }
            >
              <InboxesSuggestionsDropdownItemIcon iconName="searchSmall" />
              <InboxesSuggestionsDropdownItemPrimaryContent>
                {query}
              </InboxesSuggestionsDropdownItemPrimaryContent>
            </InboxesSuggestionsDropdownItem>
          </InboxesSuggestionsDropdownSection>
        </InboxesSuggestionsDropdownSectionWrapper>
      )}
      {filteredRecentQueries && maybeRenderRecentQueries(query, filteredRecentQueries, onSuggestionSelect)}
    </>
  );
};

/*
 * Helpers.
 */

function maybeRenderRecentQueries(
  query: string | undefined,
  filteredRecentQueries: Array<string>,
  onSuggestionSelect: SuggestionSelectHandler,
) {
  // If no recent query starts with the current one, do not display them.
  if (!filteredRecentQueries.length) {
    return null;
  }

  return (
    <InboxesSuggestionsDropdownSectionWrapper>
      <InboxesSuggestionsDropdownSection>
        <InboxesSuggestionsDropdownSectionTitle>
          <Trans id="inboxes-search-recent-queries">Recent searches</Trans>
        </InboxesSuggestionsDropdownSectionTitle>
        {filteredRecentQueries.slice(0, MAX_DISPLAYED_RECENT_QUERIES).map((recentQuery) => (
          <InboxesSuggestionsDropdownItem
            key={recentQuery}
            onSelect={(e, key) =>
              onSuggestionSelect(recentQuery, key, {
                insertWhiteSpace: false,
                replaceWholeQuery: true,
                isRecentQuery: true,
              })
            }
          >
            <InboxesSuggestionsDropdownItemIcon iconName="searchSmall" />
            <InboxesSuggestionsDropdownItemPrimaryContent>
              <FuzzyHighlight query={query} text={recentQuery} />
            </InboxesSuggestionsDropdownItemPrimaryContent>
          </InboxesSuggestionsDropdownItem>
        ))}
      </InboxesSuggestionsDropdownSection>
    </InboxesSuggestionsDropdownSectionWrapper>
  );
}
